<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <!-- We've used 3xl here, but feel free to try other max-widths based on your needs -->
    <div class="max-w-3xl py-24 mx-auto">
      <router-link to="/dashboard" class="outline-none inline-flex justify-center items-center text-sm mb-6">
        <b-icon pack="fal" class="mr-2" icon="arrow-left" />
        <span>
          Back to Dashboard
        </span>
      </router-link>
      <form @submit.prevent>
        <h2 class="text-2xl font-bold leading-7 text-gray-700 sm:text-3xl sm:leading-9 sm:truncate mb-8">
          Account Information
        </h2>
        <div>
          <div>
            <p class="mt-1 text-sm leading-5 text-gray-500">
              This information may be displayed to the Phaneroo Pastoral team
            </p>
          </div>
          <div class="mt-8 border-t border-gray-200 pt-8">
            <div>
              <h3 class="text-xl leading-6 font-bold font-medium text-gray-900 mb-4">
                Personal Information
              </h3>

              <div class="sm:col-span-6">
                <label for="photo" class="block font-bold text-md leading-5 font-medium text-gray-700 mt-10">
                  Photo
                </label>
                <div class="mt-0 flex items-center">
                  <label ref="avatarLabel" for="avatar" class="border rounded-full border-transparent focus:outline-none focus:border-orange-300 focus:shadow-outline-orange transition ease-in-out duration-150">
                    <img
                      v-if="user.avatarUrl"
                      class="inline-block h-12 w-12 rounded-full border-white border-2 cursor-pointer"
                      :src="user.avatarUrl"
                      :alt="`${user.firstName} ${user.lastName} photo`"
                    />
                    <b-icon
                      v-else
                      icon="user-alt"
                      class="inline-block text-white border-white border-2 bg-gray-100 rounded-full cursor-pointer"
                    />
                    <input id="avatar" type="file" @change="uploadAvatar" hidden ref="avatarInput" />
                  </label>
                  <span class="ml-5 rounded-md shadow-sm">
                    <b-button @click="$refs.avatarLabel.click()" type="is-light">Change</b-button>
                  </span>
                </div>
              </div>
            </div>
            <div class="mt-6 grid grid-cols-1 row-gap-6 col-gap-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <b-field label="First name">
                  <b-input v-model="firstName"></b-input>
                </b-field>
              </div>

              <div class="sm:col-span-3">
                <b-field label="Last name">
                  <b-input v-model="lastName"></b-input>
                </b-field>
              </div>

              <div class="sm:col-span-4">
                <b-field label="Email">
                  <b-input v-model="email"></b-input>
                </b-field>
              </div>

              <div class="sm:col-span-4">
                <b-field label="Phone number">
                  <b-input type="tel" v-model="phoneNumber"></b-input>
                </b-field>
              </div>
            </div>
          </div>
          <div class="mt-8 border-t border-gray-200 pt-8">
            <div>
              <h3 class="text-lg font-bold leading-6 font-medium text-gray-900">
                Password Management
              </h3>
              <b-button outlined tag="router-link" to="/account/changepassword" class="mt-4" type="is-primary">
                Reset password
              </b-button>
            </div>
          </div>
        </div>
        <div class="mt-8 border-t border-gray-200 pt-5">
          <div class="flex justify-end">
            <span class="ml-3 inline-flex rounded-md shadow-sm">
              <b-button @click="saveChanges" class="mt-4" type="is-primary">
                Save changes
              </b-button>
            </span>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>

export default {
  name: 'ProfilePage',
  inject: ['$user'],
  data () {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      noop: 0
    }
  },
  computed: {
    user () {
      return this.$user()
    },
    userType () {
      if (this.user.isAdmin) return 'Admin'
      else if (this.user.isPastor) return 'Pastor'
      else if (this.user.isMember) return 'Member'
      else return 'Member'
    }
  },
  watch: {
    user: {
      immediate: true,
      handler (newVal, oldVal) {
        if (newVal === oldVal) return
        this.firstName = this.user.firstName
        this.lastName = this.user.lastName
        this.email = this.user.email
        this.phoneNumber = this.user.phoneNumber
        this.noop++
      }
    }
  },
  methods: {
    /**
     * Save user object changes
     */
    async saveChanges () {
      const user = {
        ...this.user,
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        phoneNumber: this.phoneNumber
      }

      try {
        await this.$store.dispatch('users/updateUser', user)
        await this.$store.dispatch('users/getUserById', user.id)

        this.$notify('Profile updated!', {
          type: 'success'
        })
      } catch (error) {
        this.$sentry.captureException(error)
        this.$notify(error.message + '. Please try again later.', {
          title: 'Error updating profile',
          type: 'error'
        })
        this.$logger.error('Error updating user object', error)
      }
    },
    /**
     * Uploads new avatar for user
     */
    async uploadAvatar () {
      try {
        const avatar = this.$refs.avatarInput.files[0]
        const fd = new FormData()
        const file = new File([avatar], `${this.user.firstName}_${this.user.lastName}_avatar.jpeg`)
        fd.append('avatar', file)

        await this.$store.dispatch('users/updateAvatar', {
          userId: this.user.id,
          formData: fd
        })

        await this.$store.dispatch('users/getUserById', this.user.id)
        this.$notify('You successfully updated your avatar!', {
          title: '👍🏽 Avatar updated',
          type: 'success'
        })
      } catch (error) {
        this.$sentry.captureException(error)
        this.$notify('We could not update your avatar at this moment.', {
          type: 'error'
        })
        this.$logger.error('Error updating avatar', error)
      }
    }
  }
}
</script>
